import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import {useState} from "react";
import DuprPartnerBadge from "../components/DuprPartnerBadge.tsx";
import {GeneratorProgress, GeneratorTopCta, useStagedGenerate} from "../components/generatorReveal.tsx";
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";
import TournamentManagementCta from "../landing/sections/TournamentManagementCta.tsx";

export const Route = createFileRoute('/ladder-league-generator')({
    component: RouteComponent,
})

type LadderRow = {rank: number; player: string; status: string};
type Challenge = {player: string; rank: number; canChallenge: string[]};

function buildLadder(players: string[], range: number): {ladder: LadderRow[]; challenges: Challenge[]} {
    const ladder: LadderRow[] = players.map((player, index) => ({
        rank: index + 1,
        player,
        status: index === 0 ? "Top of ladder" : "Can challenge above",
    }));

    const challenges: Challenge[] = [];
    for (let i = 1; i < players.length; i++) {
        const canChallenge: string[] = [];
        for (let offset = 1; offset <= range; offset++) {
            const target = i - offset;
            if (target >= 0) canChallenge.push(players[target]);
        }
        challenges.push({player: players[i], rank: i + 1, canChallenge});
    }

    return {ladder, challenges};
}

const STEPS = [
    {title: "Create Rankings", description: "Start with seeded, random or existing player rankings."},
    {title: "Allow Challenges", description: "Players challenge others above them within a set range."},
    {title: "Move Up or Down", description: "Winners climb the ladder while rankings update over time."},
];

const VS_ROWS = [
    {feature: "Best for ongoing play", la: "Yes", rr: "Sometimes"},
    {feature: "Everyone plays everyone", la: "No", rr: "Yes"},
    {feature: "Flexible scheduling", la: "Yes", rr: "No"},
    {feature: "Clear rankings", la: "Yes", rr: "Yes"},
    {feature: "Best for club members", la: "Yes", rr: "Yes"},
];

const BEST_FOR = ["Padel clubs", "Tennis clubs", "Pickleball groups", "Squash clubs", "Ongoing member competitions", "Flexible weekly challenges"];
const LESS_IDEAL = ["One-day tournaments", "Formal knockout events", "Events with a fixed final", "Short competitions with limited time", "Formats where everyone must play everyone"];

const FAQS = [
    {
        q: 'What is a ladder league?',
        a: <>A ladder league is a competition where players are ranked in order and move up or down the ladder based on match results.</>,
    },
    {
        q: 'How do ladder league challenges work?',
        a: <>Players usually challenge opponents ranked above them within a defined range, such as one to three positions higher.</>,
    },
    {
        q: 'What happens when a lower-ranked player wins?',
        a: <>The lower-ranked player usually moves above the player they defeated, depending on the league rules.</>,
    },
    {
        q: 'Is a ladder league good for padel or pickleball?',
        a: <>Yes. Ladder leagues work very well for padel, pickleball, tennis and squash clubs because they support flexible, ongoing competition.</>,
    },
    {
        q: 'Can ScorePal manage ladder league rankings?',
        a: <>Yes. ScorePal manages challenges, fixtures, scores, player rankings and league communication.</>,
    },
];

function RouteComponent() {
    const [playersText, setPlayersText] = useState("");
    const [range, setRange] = useState("2");
    const [result, setResult] = useState<{ladder: LadderRow[]; challenges: Challenge[]} | null>(null);
    const [summary, setSummary] = useState<{players: number; range: number} | null>(null);
    const [error, setError] = useState<string | null>(null);

    const gen = useStagedGenerate(["Ordering the starting ladder", "Setting challenge ranges", "Preparing opening challenges"]);

    const generate = () => {
        const players = playersText.split("\n").map((n) => n.trim()).filter(Boolean);
        if (players.length < 2) {
            setError("Please enter at least two players.");
            setResult(null);
            setSummary(null);
            return;
        }
        const r = Math.min(3, Math.max(1, parseInt(range, 10) || 2));
        setError(null);
        setResult(null);
        setSummary(null);
        gen.run(() => {
            setResult(buildLadder(players, r));
            setSummary({players: players.length, range: r});
        });
    };

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Ladder League Generator (Free) | ScorePal</title>
            <meta name="description" content="Create a ladder league schedule and rankings instantly. Generate player ladders for padel, tennis, pickleball, football, basketball, baseball, cricket, rugby and club competitions with ScorePal's free ladder league generator." />
            <link rel="canonical" href={`${config.publicUrl}/ladder-league-generator/`} />
            <meta property="og:title" content="Ladder League Generator | ScorePal" />
            <meta property="og:description" content="Generate ladder league rankings and fixtures for padel, tennis, pickleball, football, basketball, cricket and club competitions." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/ladder-league-generator/`} />
            <meta property="og:image" content="https://scorepal.co/images/ladder-league-generator-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Ladder League Generator | ScorePal" />
            <meta name="twitter:description" content="Create ladder league schedules and rankings instantly with ScorePal." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "Ladder League Generator",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "A free online tool to generate ladder league rankings and fixtures for sports clubs and competitions.",
                "url": "https://scorepal.co/ladder-league-generator/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
                "offers": {"@type": "Offer", "price": "0", "priceCurrency": "USD"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is a ladder league?", "acceptedAnswer": {"@type": "Answer", "text": "A ladder league is a competition format where players are ranked in a ladder and move up or down based on match results."}},
                    {"@type": "Question", "name": "How does a ladder league work?", "acceptedAnswer": {"@type": "Answer", "text": "Players challenge nearby players in the ladder. Winners move up the rankings and losers may move down depending on the rules."}},
                    {"@type": "Question", "name": "What sports use ladder leagues?", "acceptedAnswer": {"@type": "Answer", "text": "Ladder leagues are popular in tennis, padel, pickleball, football, basketball, baseball, cricket, rugby and other club sports."}},
                    {"@type": "Question", "name": "What happens when a lower-ranked player wins?", "acceptedAnswer": {"@type": "Answer", "text": "The lower-ranked player usually moves above the player they defeated, depending on the league rules."}},
                    {"@type": "Question", "name": "Can ScorePal manage ladder league rankings?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal manages challenges, fixtures, scores, player rankings and league communication."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Ladder League Generator", item: "https://scorepal.co/ladder-league-generator/"}]} />

            {/* Hero + generator */}
            <section className="relative overflow-hidden p-4">
                <DuprPartnerBadge />
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Free League Tool
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-5">
                                    Ladder League Generator
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    Create a ladder league for padel, tennis, pickleball, football, basketball, baseball, cricket, rugby and club competitions.
                                    Generate rankings, challenge fixtures and a simple structure for players to move up and
                                    down the ladder.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Create Tournament</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <Link to={"/tournament-formats/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Compare Formats
                                    </Link>
                                </div>
                            </div>

                            {/* Generator card */}
                            <div id="generator" className="bg-white rounded-3xl p-6 lg:p-8 shadow-xl scroll-mt-24">
                                <h2 className="font-bebas text-[#1a1a2e] uppercase text-3xl font-bold tracking-wide mb-1">Create Your Ladder</h2>
                                <p className="font-inter text-[#1a1a2e]/60 text-sm mb-5">Enter your players in starting order and set the challenge range.</p>

                                <label htmlFor="players" className="block font-inter font-semibold text-[#1a1a2e] mb-2">Players</label>
                                <textarea
                                    id="players"
                                    value={playersText}
                                    onChange={(e) => setPlayersText(e.target.value)}
                                    placeholder={"Enter one player per line\nPlayer 1\nPlayer 2\nPlayer 3\nPlayer 4\nPlayer 5\nPlayer 6"}
                                    className="w-full min-h-[150px] resize-y rounded-xl border border-[#e5e7eb] p-4 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40"
                                />

                                <label htmlFor="range" className="block font-inter font-semibold text-[#1a1a2e] mt-4 mb-2">Challenge range</label>
                                <select
                                    id="range"
                                    value={range}
                                    onChange={(e) => setRange(e.target.value)}
                                    className="w-full rounded-xl border border-[#e5e7eb] p-3.5 font-inter text-[#1a1a2e] bg-white focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40"
                                >
                                    <option value="1">Challenge 1 place above</option>
                                    <option value="2">Challenge up to 2 places above</option>
                                    <option value="3">Challenge up to 3 places above</option>
                                </select>

                                <button
                                    type="button"
                                    onClick={generate}
                                    disabled={gen.running}
                                    className="mt-5 w-full rounded-xl bg-[#082F8A] py-3.5 font-inter font-bold text-white transition hover:bg-[#06246b] disabled:opacity-70"
                                >
                                    {gen.running ? "Generating…" : "Generate Ladder League"}
                                </button>

                                <GeneratorProgress gen={gen} />

                                {error && <p className="mt-3 font-inter text-sm text-red-600">{error}</p>}
                                {summary && (
                                    <p className="mt-4 font-inter text-sm text-[#1a1a2e]/70">
                                        <strong>{summary.players}</strong> players · challenge up to{" "}
                                        <strong>{summary.range}</strong> place{summary.range > 1 ? "s" : ""} above
                                    </p>
                                )}

                                {result && <GeneratorTopCta note="Run it live on ScorePal with challenges, results and auto-updating rankings." />}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Results */}
            {result && (
                <section className="py-12 lg:py-16 bg-white">
                    <div className="container mx-auto px-6 lg:px-12">
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                            Your Ladder League
                        </h2>

                        {/* Ladder table */}
                        <h3 className="font-inter font-semibold text-[#1a1a2e] mb-3">Starting Ladder Rankings</h3>
                        <div className="overflow-x-auto mb-10">
                            <div className="min-w-[480px] border border-[#d9dce7] rounded-2xl overflow-hidden bg-white shadow-sm">
                                <div className="grid grid-cols-[0.5fr_1.5fr_1.2fr] bg-[#F6F7FB] font-bebas text-lg text-[#1a1a2e]">
                                    <div className="p-4 border-r border-[#d9dce7]">Rank</div>
                                    <div className="p-4 border-r border-[#d9dce7]">Player</div>
                                    <div className="p-4">Status</div>
                                </div>
                                {result.ladder.map((row, i) => (
                                    <div key={row.player + i} className={`grid grid-cols-[0.5fr_1.5fr_1.2fr] border-t border-[#d9dce7] font-inter text-[#1a1a2e] items-center ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                        <div className="p-4 border-r border-[#d9dce7]">
                                            <span className="inline-flex items-center justify-center min-w-9 h-9 rounded-full bg-[#EDEEF4] text-[#082F8A] font-bold">{row.rank}</span>
                                        </div>
                                        <div className="p-4 border-r border-[#d9dce7] font-semibold">{row.player}</div>
                                        <div className="p-4 text-[#1a1a2e]/80">{row.status}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Challenges */}
                        <div className="bg-[#F6F7FB] border border-[#e5e7eb] rounded-2xl p-6">
                            <div className="font-bebas text-xl text-[#082F8A] font-bold mb-4">Suggested Challenges</div>
                            <div className="space-y-2.5">
                                {result.challenges.map((c, i) => (
                                    <div key={i} className="bg-white border border-[#e5e7eb] rounded-xl p-3 font-inter text-sm flex items-center justify-between gap-4 flex-wrap">
                                        <div>
                                            <span className="font-semibold text-[#1a1a2e]">{c.player}</span>
                                            <span className="text-[#1a1a2e]/50 ml-2">Rank {c.rank}</span>
                                        </div>
                                        <div className="text-[#1a1a2e]/70">
                                            can challenge <span className="font-semibold text-[#1a1a2e]">{c.canChallenge.join(", ")}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-10 flex justify-center">
                            <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#082F8A] text-white rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-[#06246b] transition">
                                <span className="font-inter text-base font-medium">Run this league with ScorePal</span>
                                <div className="bg-white rounded-full p-1">
                                    <ArrowUpRightIcon className="text-[#082F8A]" size={28} />
                                </div>
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* What is a ladder league / steps */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                        What Is a Ladder League?
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed max-w-4xl">
                        A ladder league is a flexible competition format where players are ranked from top to bottom and move
                        up or down based on match results. Players usually challenge opponents ranked nearby, making it ideal
                        for ongoing club competitions.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
                        {STEPS.map((step, index) => (
                            <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                                <div className="w-10 h-10 rounded-full bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{index + 1}</div>
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{step.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Ladder vs Round Robin */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Ladder League vs Round Robin
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        Ladder leagues are more flexible than <Link to={"/round-robin-tournament-generator/" as any} className="text-[#082F8A] underline font-semibold">round robin</Link> tournaments and are especially useful for ongoing club play.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[640px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Feature</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#082F8A]">Ladder League</div>
                                <div className="p-5 text-[#1a1a2e]">Round Robin</div>
                            </div>
                            {VS_ROWS.map((row, i) => (
                                <div key={row.feature} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/80">{row.feature}</div>
                                    <div className="p-5 border-r border-[#d9dce7] font-medium">{row.la}</div>
                                    <div className="p-5 text-[#1a1a2e]/80">{row.rr}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* When to use */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                        When Should You Use a Ladder League?
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="font-inter font-semibold text-[#12b76a] mb-3">Best For</h3>
                            <ul className="list-disc pl-5 space-y-2 font-inter text-sm text-[#1a1a2e]/80">
                                {BEST_FOR.map((x) => <li key={x}>{x}</li>)}
                            </ul>
                        </div>
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h3 className="font-inter font-semibold text-[#d92d20] mb-3">Less Ideal For</h3>
                            <ul className="list-disc pl-5 space-y-2 font-inter text-sm text-[#1a1a2e]/80">
                                {LESS_IDEAL.map((x) => <li key={x}>{x}</li>)}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run Your Ladder League With ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Manage rankings, challenges, scores and player movement from one simple platform built for clubs, leagues and organizers.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Ladder Leagues", "Padel", "Tennis", "Football", "Cricket", "Player Rankings"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Ladder League FAQs"} faqs={FAQS} />
            <TournamentManagementCta />
            <FooterSection />
        </div>
    );
}
